import { isExternalUrl, toPath } from '~/utils/urlHelpers'

/**
 * Returns a resolver for navigation link hrefs (used by the megamenu).
 * - External URLs (different host) are returned as-is for use in `<a href>`.
 * - Internal hrefs are returned as localized, origin-relative paths. The
 *   megamenu renders them as plain `<a>` tags, so each click is a full-page,
 *   same-origin navigation. Keeping the href relative means links stay on the
 *   host actually serving the page: this fixes menu links jumping to the base
 *   deployment on Vercel preview URLs (where BASE_URL points at a different
 *   host than the preview branch). The path is localized up front so the
 *   browser goes straight to the locale-prefixed URL instead of taking a
 *   redirect hop.
 *
 * Assumes internal hrefs are relative and unlocalized (the megamenu data
 * contract in i18n/translations/*\/megamenu.ts).
 */
export const useFullLinkUrl = () => {
  const { baseUrl } = useRuntimeConfig().public as { baseUrl: string }
  const localePath = useLocalePath()

  return (href: string): string => {
    if (isExternalUrl(href, baseUrl)) return href
    // Localize the pathname only, preserving any query string / hash suffix
    // (localePath resolves the route and can otherwise drop the query).
    const path = toPath(href)
    const suffixStart = path.search(/[?#]/)
    const pathname = suffixStart === -1 ? path : path.slice(0, suffixStart)
    const suffix = suffixStart === -1 ? '' : path.slice(suffixStart)
    return localePath(pathname) + suffix
  }
}
